import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { useParams, Link } from "react-router-dom";
import { Send, ChevronLeft, Clock } from "lucide-react";
import { api, fmtDateTime } from "@/lib/api";
import { Skeleton, StatusPill, PriorityDot } from "@/components/Atoms";

const fetcher = (u) => api.get(u).then((r) => r.data);

export default function BuyerTicketDetail() {
  const { id } = useParams();
  const key = `/tickets/${id}`;
  const { data } = useSWR(key, fetcher, { refreshInterval: 10000 });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tickets/${id}/messages`, { message: msg });
      setMsg("");
      mutate(key);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Link to="/buyer/tickets" className="rc-btn rc-btn-sm rc-btn-ghost -ml-3"><ChevronLeft size={16}/> All queries</Link>
      {!data && <Skeleton h={240}/>}
      {data && (
        <>
          {/* Property context strip — buyer side */}
          {data.context?.project && (
            <div className="rc-card rc-card-padded" data-testid="buyer-ticket-property">
              <div className="rc-caption uppercase tracking-wider mb-2" style={{ fontSize: 10 }}>For</div>
              <div className="flex items-center gap-2 flex-wrap rc-footnote">
                <span className="rc-headline" style={{ fontSize: 15 }}>{data.context.project?.project_name}</span>
                <span className="text-[var(--rc-label-tertiary)]">·</span>
                <span>{data.context.subproject?.subproject_name}</span>
                {data.context.flat?.flat_number && (
                  <>
                    <span className="text-[var(--rc-label-tertiary)]">·</span>
                    <span>Flat {data.context.flat.flat_number}</span>
                  </>
                )}
              </div>
            </div>
          )}
          <div className="rc-card rc-card-padded">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><PriorityDot priority={data.ticket.priority}/>
                  <div className="rc-title-2 break-words">{data.ticket.subject}</div></div>
                <div className="rc-footnote mt-1">{data.ticket.category} · #{data.ticket.ticket_id.slice(0,8)}</div>
              </div>
              <div className="text-right">
                <StatusPill status={data.ticket.sla_breached && !["resolved","closed"].includes(data.ticket.status) ? "breached" : data.ticket.status} />
                <div className="rc-caption mt-1 flex items-center justify-end gap-1"><Clock size={11}/>SLA {fmtDateTime(data.ticket.sla_deadline)}</div>
              </div>
            </div>
            <div className="rc-body mt-3 whitespace-pre-wrap">{data.ticket.description}</div>
          </div>

          <div className="rc-card rc-card-padded">
            <div className="rc-title-3 mb-3">Conversation</div>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
              {data.updates.map((u) => (
                <div key={u.update_id} className={`flex ${u.updated_by_type === "buyer" ? "justify-end" : "justify-start"}`}>
                  <div>
                    <div className={`rc-bubble ${u.updated_by_type === "buyer" ? "rc-bubble-customer" : "rc-bubble-team"}`}>{u.update_text}</div>
                    <div className="rc-caption mt-1 text-center">{u.updated_by_name || u.updated_by_type} · {fmtDateTime(u.created_at)}</div>
                  </div>
                </div>
              ))}
              {data.updates.length === 0 && <div className="rc-footnote text-center py-6">No messages yet.</div>}
            </div>

            <div className="mt-4 flex gap-2 items-center">
              <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                className="rc-input flex-1" placeholder="Type a reply…" data-testid="reply-input"/>
              <button onClick={send} disabled={busy || !msg.trim()} className="rc-btn rc-btn-primary rc-btn-sm" data-testid="reply-send"><Send size={16}/></button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
